import { gql } from '@apollo/client';
import Raven from 'raven-js';
import { PAYMENT_PARAM_SBP } from 'app/const/billing-payment-methods';

import {
    getNextPeriodToInvoice,
    clearDishAddedPeriod,
} from 'app/views/Basket/components/BasketNewUpsell/components/BasketTopDishCard/addedPeriodStorage';

import {
    getIntervalsForDate,
    prioritizeDatesByWeekday,
    pickSimilarInterval,
} from './nextPeriodInvoiceSlot.utils';

/**
 * @description сколько дат проверяем подряд, прежде чем решить,
 * что в цикле нет доставки
 */
const DATES_LIMIT = 5;

/**
 * @note сырые поля вместо @client-полей deliveryCalendar.availableDates
 * и intervals.deliveryTimesList: оба @client-резолвера читают состояние
 * основного чекаута (intervals:user-session и checkoutDeliveryDateVar),
 * из-за чего для чужого периода отдали бы интервалы основной даты.
 */
const NEXT_PERIOD_DELIVERY_DATES = gql`
    query NEXT_PERIOD_DELIVERY_DATES ($period: String!) {
        deliveryDates(period: $period) {
            available_dates
            disabled_dates
        }
    }
`;

const NEXT_PERIOD_DELIVERY_INTERVALS = gql`
    query NEXT_PERIOD_DELIVERY_INTERVALS ($date: String) {
        deliveryIntervals(date: $date) {
            region_name
            has_morning_deliveries
            has_evening_deliveries
            morning_intervals {
                time_from
                time_to
            }
            evening_intervals {
                time_from
                time_to
            }
            days_of_week_for_morning_delivery
            days_of_week_for_evening_delivery
        }
    }
`;

const fetchAvailableDates = async ({ client, period }) => {
    const { data } = await client.query({
        query: NEXT_PERIOD_DELIVERY_DATES,
        variables: { period },
        fetchPolicy: 'network-only',
    });

    const {
        available_dates: availableDates = [],
        disabled_dates: disabledDates = [],
    } = data?.deliveryDates || {};

    return availableDates.filter((date) => !disabledDates.includes(date));
};

const fetchIntervals = async ({ client, date }) => {
    const { data } = await client.query({
        query: NEXT_PERIOD_DELIVERY_INTERVALS,
        variables: { date },
        fetchPolicy: 'network-only',
    });

    return data?.deliveryIntervals;
};

/**
 * @description дата цикла с доставкой, максимально похожая на основной инвойс:
 * сперва проверяется тот же день недели, и уже в нём — тот же интервал.
 * Аналогии нет — берётся первая доступная дата и первый доступный интервал.
 * Интервалы запрашиваются по очереди и только до первой подходящей даты:
 * второй запрос нужен лишь когда выпал неденеверительный день недели.
 */
const resolveDeliverySlot = async ({
    client,
    dates,
    baseDate,
    baseTime,
}) => {
    /* @note приоритетная дата идёт первой, поэтому не выпадает из лимита */
    const datesToCheck = prioritizeDatesByWeekday(dates || [], baseDate).slice(0, DATES_LIMIT);

    for (let i = 0; i < datesToCheck.length; i += 1) {
        const date = datesToCheck[i];

        /* eslint-disable-next-line no-await-in-loop */
        const intervalsData = await fetchIntervals({ client, date });
        const intervals = getIntervalsForDate(date, intervalsData);

        if (intervals.length) {
            return {
                deliveryDate: date,
                deliveryTime: pickSimilarInterval(intervals, baseTime),
                regionName: intervalsData?.region_name,
            };
        }
    }

    return null;
};

/**
 * @description доп. инвойс на корзину следующего цикла с оплатой курьеру по QR.
 * Вызывается из Checkout.createInvoice сразу после создания основного инвойса.
 *
 * @note baseVariables — переменные основного инвойса; его delivery_date и
 * delivery_time служат образцом даты и интервала для следующего цикла
 * @note полностью изолирован от основного флоу: свои запросы дат/интервалов,
 * не трогает реактивные переменные чекаута, не пишет инвойс в кеш
 * и не бросает ошибки наружу — основной заказ не должен пострадать.
 * @note вызывается без await, поэтому все ошибки, включая сетевые,
 * остаются внутри и попадают только в Raven
 * @returns {Promise<{uid: string, price: number, deliveryDate: string} | null>}
 */
export const createNextPeriodInvoice = async ({
    client,
    createInvoice,
    shipping,
    baseVariables,
    address,
    currentPeriod,
}) => {
    const nextPeriod = getNextPeriodToInvoice();

    if (!nextPeriod || nextPeriod === currentPeriod) return null;

    /* period/дата/интервал/способ оплаты/теги задаются ниже,
     * дата и интервал основного инвойса идут в resolveDeliverySlot */
    const {
        period: _period,
        delivery_date: baseDeliveryDate,
        delivery_time: baseDeliveryTime,
        delivery_region_name: _regionName,
        payment_method: _paymentMethod,
        tags: _tags,
        ...sharedVariables
    } = baseVariables;

    try {
        /* @note shipping и запрос дат независимы: даты скоуплены на период,
         * а не на адрес, поэтому идут одним батчем. */
        const [, dates] = await Promise.all([
            /* адрес в createInvoice не передаётся, он живёт в состоянии периода */
            shipping({
                variables: { period: nextPeriod, address },
            }),
            fetchAvailableDates({ client, period: nextPeriod }),
        ]);

        const slot = await resolveDeliverySlot({
            client,
            dates,
            baseDate: baseDeliveryDate,
            baseTime: baseDeliveryTime,
        });

        if (!slot) return null;

        const { data } = await createInvoice({
            variables: {
                ...sharedVariables,
                period: nextPeriod,
                delivery_date: slot.deliveryDate,
                delivery_time: slot.deliveryTime,
                delivery_region_name: slot.regionName,
                payment_method: PAYMENT_PARAM_SBP,
            },
        });

        const invoice = data?.createInvoice;

        if (!invoice?.uid) return null;

        clearDishAddedPeriod(nextPeriod);

        return {
            uid: invoice.uid,
            price: invoice.price,
            deliveryDate: slot.deliveryDate,
        };
    } catch (error) {
        /* @note напр. 'Total price in cart cannot be 0', если блюдо убрали из корзины */
        Raven.captureException(error, { extra: { nextPeriod } });
        return null;
    }
};
